import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ExternalLink, RefreshCw, Receipt, CreditCard } from 'lucide-react';
import { Button } from '../../../components/actions/Button';
import { useSubscription, usePlans, usePortal, useBillingUsage } from '../../../api/hooks/useBilling';

export function OverviewTab() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');

  const { data: subscription, isLoading: loadingSubscription } = useSubscription();
  const { data: plans } = usePlans();
  const { data: usage } = useBillingUsage();
  const portal = usePortal();

  const planner = plans?.subscriptions?.planner;
  const isPlannerSubscribed = subscription?.subscribed && planner?.stripe_price_yearly === subscription.plan;
  const paidInviteCount = usage?.usage?.paid_invites?.length ?? 0;

  const handleManageSubscription = () => {
    setErrorMessage('');
    portal.mutate(undefined, {
      onError: () => {
        setErrorMessage('No billing history yet. Make a purchase or subscribe to access the billing portal.');
      },
    });
  };

  const handleViewPlans = () => {
    navigate('/app/billing?tab=plans');
  };

  if (loadingSubscription) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const isActive = subscription?.subscribed || subscription?.on_trial || subscription?.on_grace_period;

  return (
    <div className="max-w-4xl">
      {/* Error Message */}
      {errorMessage && (
        <div className="flex items-center gap-2 p-4 mb-6 bg-error/10 border border-error/20 rounded-lg text-error">
          <AlertTriangle size={20} />
          {errorMessage}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Status Card */}
        <div className="bg-background-card border border-border rounded-xl p-6 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary">Billing Status</h2>
            {isPlannerSubscribed && isActive && (
              <span className="px-3 py-1 bg-success/10 text-success text-sm font-medium rounded-full">
                Planner - Active
              </span>
            )}
          </div>

          {isPlannerSubscribed ? (
            <div className="space-y-4">
              <div>
                <p className="text-2xl font-bold text-text-primary">Planner</p>
                <p className="text-sm text-text-secondary">
                  £{planner?.price_yearly}/year — up to 10 live invites
                </p>
              </div>

              {subscription?.on_trial && subscription.trial_ends_at && (
                <div className="inline-flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg text-primary text-sm">
                  <AlertTriangle size={16} />
                  Trial ends {new Date(subscription.trial_ends_at).toLocaleDateString()}
                </div>
              )}

              {subscription?.on_grace_period && subscription.ends_at && (
                <div className="inline-flex items-center gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg text-warning text-sm">
                  <AlertTriangle size={16} />
                  Canceled - access until {new Date(subscription.ends_at).toLocaleDateString()}
                </div>
              )}

              {subscription?.current_period_end && !subscription.on_grace_period && isActive && (
                <p className="text-sm text-text-secondary">
                  Renews {new Date(subscription.current_period_end).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-2xl font-bold text-text-primary">Pay per invite</p>
                <p className="text-sm text-text-secondary">
                  {paidInviteCount > 0
                    ? `${paidInviteCount} invite${paidInviteCount !== 1 ? 's' : ''} purchased`
                    : 'Purchase invites individually or subscribe for unlimited publishing'
                  }
                </p>
              </div>

              <Button onClick={handleViewPlans}>
                View Plans
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Billing Management Cards */}
      <div className="mt-6 grid md:grid-cols-3 gap-6">
        {/* Billing Portal */}
        <div className="bg-background-card border border-border rounded-xl p-6">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <ExternalLink className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-base font-semibold text-text-primary mb-2">
            Billing Portal
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Manage your subscription and billing settings
          </p>
          <Button
            onClick={handleManageSubscription}
            disabled={portal.isPending}
            variant="secondary"
            fullWidth
          >
            {portal.isPending ? 'Opening...' : 'Open Portal'}
          </Button>
        </div>

        {/* View Invoices */}
        <div className="bg-background-card border border-border rounded-xl p-6">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <Receipt className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-base font-semibold text-text-primary mb-2">
            View Invoices
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Download past invoices and payment history
          </p>
          <Button
            onClick={handleManageSubscription}
            disabled={portal.isPending}
            variant="secondary"
            fullWidth
          >
            {portal.isPending ? 'Opening...' : 'View Invoices'}
          </Button>
        </div>

        {/* Change Payment Method */}
        <div className="bg-background-card border border-border rounded-xl p-6">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <CreditCard className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-base font-semibold text-text-primary mb-2">
            Payment Method
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Update your credit card or payment details
          </p>
          <Button
            onClick={handleManageSubscription}
            disabled={portal.isPending}
            variant="secondary"
            fullWidth
          >
            {portal.isPending ? 'Opening...' : 'Update Payment'}
          </Button>
        </div>
      </div>
    </div>
  );
}
